import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-store.js';
import { usePlatformAuth } from '../lib/platform-store.js';
import { Spinner } from './ui/spinner.js';

/**
 * Route guards. These are a UX convenience ONLY: every endpoint authorizes
 * server-side (SRS §3). Hiding a route never substitutes for that check.
 */

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const status = useAuth((s) => s.status);
  const hydrate = useAuth((s) => s.hydrate);
  const location = useLocation();

  useEffect(() => {
    if (status === 'unknown') void hydrate();
  }, [status, hydrate]);

  if (status === 'unknown') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Restoring your session" />
      </div>
    );
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

export function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: string[];
  children: ReactNode;
}): JSX.Element {
  const can = useAuth((s) => s.can);
  if (!anyOf.some((p) => can(p))) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function RequirePlatform({ children }: { children: ReactNode }): JSX.Element {
  const status = usePlatformAuth((s) => s.status);
  const hydrate = usePlatformAuth((s) => s.hydrate);

  useEffect(() => {
    if (status === 'unknown') void hydrate();
  }, [status, hydrate]);

  if (status === 'unknown') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Restoring your session" />
      </div>
    );
  }
  if (status === 'anonymous') return <Navigate to="/platform/login" replace />;
  return <>{children}</>;
}
